import {
  Button,
  Checkbox,
  FilterContext,
  Panel,
  PanelBody,
  PanelRow,
  Select,
} from '@teamsnap/teamsnap-ui';
import { FormColumn } from 'components/registration/v2/RegistrationParticipantList';
import { chunk, isNil } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import BasicSearch from '../table/basic-search';

interface BulkAction {
  label: string;
  onSelected: (selected: any) => void;
  disabled?: boolean;
}

interface Props {
  onSearch: (searchTerm: string) => void;
  searchTerm: string;
  filters: any[];
  customSearchFilter?: any;
  isDefaultExpanded?: boolean;
  bulkActions?: any[];
  isExportButtonDisabled?: boolean;
  activeFilters: Record<string, string[]>;
  setActiveFilters: (f: Record<string, string[]>) => void;
  columns: FormColumn[];
  onExport?: () => void;
  selectedRows: any[];
  selectedColumns?: string[];
  setSelectedColumns?: (columnNames: string[]) => void;
  placeholder?: string;
  onOpenColumns?: () => void;
}

// TODO: Update this component to use Snap-UI Filters and SearchBar components
// when that becomes available!

// For now, this file is mostly a copy+paste of the filter logic from paginated-table.
export function FilterSearchBar({
  onSearch,
  customSearchFilter,
  bulkActions,
  placeholder,
  selectedColumns,
  setSelectedColumns,
  columns,
  activeFilters,
  onOpenColumns,
  isExportButtonDisabled,
  searchTerm,
  selectedRows,
  filters,
  onExport,
  setActiveFilters,
  isDefaultExpanded,
}: Props) {
  const [columnsFlyoutOpen, setColumnsFlyoutOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(isDefaultExpanded);

  React.useEffect(() => {
    if (!columnsFlyoutOpen) {
      window.removeEventListener('click', handleBodyClick, false);
    } else {
      window.addEventListener('click', handleBodyClick, false);
    }
  }, [columnsFlyoutOpen]);

  const requiredColumns = columns
    .filter((column) => column.isRequired)
    .map((col) => col.name);

  const handleBodyClick = React.useCallback((e) => {
    e.stopPropagation();
    const isTargetingPopup = e.target.closest('#manage-columns') !== null;
    if (!isTargetingPopup) {
      setColumnsFlyoutOpen(false);
    }
  }, []);

  const bulkActionFuncsByLabel = bulkActions?.reduce(
    (acc, action: BulkAction) => {
      acc[action.label] = action.onSelected;
      return acc;
    },
    {}
  );

  const filterLength = Object.values(activeFilters).filter(
    (x) => !isNil(x) && x.length
  ).length;

  function updateSearchFilter({ searchTerm: searchValue }) {
    onSearch(searchValue);
  }

  const handleManageColumnsClick = () => {
    setColumnsFlyoutOpen(!columnsFlyoutOpen);
    if (onOpenColumns) {
      onOpenColumns();
    }
  };

  return (
    <div>
      <div className="Grid Grid-cell u-spaceTopSm">
        {bulkActions?.length > 0 ? (
          <div className="Grid-cell u-spaceRightSm u-flex u-size1of1 u-md-size2of12 u-flexJustifyStart">
            <Select
              inputProps={{
                value: '',
                onChange: (event) => {
                  const fn = bulkActionFuncsByLabel[event.target.value];
                  if (fn) {
                    fn(selectedRows);
                  }
                },
              }}
              name="bulkActions"
              options={[
                {
                  label:
                    selectedRows.length > 0
                      ? `${selectedRows.length} selected`
                      : 'Actions',
                  value: null,
                },
                ...bulkActions.map((e) => ({
                  label: e.label,
                  value: e.label,
                  disabled: e.disabled || false,
                })),
              ]}
            />
          </div>
        ) : null}
        <div className="Grid-cell u-sizeFill u-md-size1of4">
          {customSearchFilter && customSearchFilter}

          <BasicSearch
            searchPlaceholder={placeholder}
            searchFunction={updateSearchFilter}
            searchValue={searchTerm}
          />
        </div>

        {filters.length > 0 && (
          <div>
            <Button
              testId="PaginatedTable--filter-button"
              isActive={filterLength > 0 || isDefaultExpanded}
              onClick={() => {
                setFilterOpen(!filterOpen);
              }}
              mods="Button Button--primary u-spaceLeftSm"
              icon="wrench"
            >
              Filter{' '}
              {filterLength > 0 ? (
                <span
                  className="u-bgPrimary7 u-colorNeutral1 u-fontSizeXs"
                  style={{ borderRadius: '50px', padding: '1px 4px' }}
                >
                  {filterLength}
                </span>
              ) : null}
            </Button>
          </div>
        )}

        {setSelectedColumns && (
          <div id="manage-columns">
            <Button
              testId="PaginatedTable--manage-columns-button"
              isActive={columnsFlyoutOpen}
              onClick={handleManageColumnsClick}
              mods="Button u-spaceLeftSm"
              icon="settings"
            >
              Manage Columns
            </Button>

            <Panel
              mods={`manage-columns-flyout ${
                !columnsFlyoutOpen ? 'u-hidden' : ''
              }`}
              data-testid="manage-columns-flyout"
            >
              <PanelBody>
                <PanelRow mods="Grid-cell u-flexAuto u-padBottomMd">
                  <h4 className="title">Manage Columns</h4>
                  <div className="u-flex">
                    {chunk(columns, Math.round(columns.length / 3)).map(
                      (chunks, index) => (
                        <div className="columns-section" key={index}>
                          {chunks.map(({ name, label }) => (
                            <Checkbox
                              key={`${name}-combobox`}
                              name={`${name}-combobox`}
                              label={label}
                              inputProps={{
                                disabled: requiredColumns.includes(name),
                                checked: selectedColumns.includes(name),
                                value: name,
                                onChange: () => {
                                  if (selectedColumns.includes(name)) {
                                    setSelectedColumns(
                                      selectedColumns.filter(
                                        (selectedFilter) =>
                                          selectedFilter !== name
                                      )
                                    );
                                  } else {
                                    setSelectedColumns([
                                      ...selectedColumns,
                                      name,
                                    ]);
                                  }
                                },
                              }}
                            />
                          ))}
                        </div>
                      )
                    )}
                  </div>
                </PanelRow>
              </PanelBody>
            </Panel>
          </div>
        )}

        {onExport && (
          <div data-testid="PaginatedTable--export-button">
            <Button
              isActive={!isExportButtonDisabled}
              onClick={onExport}
              mods="u-spaceLeftSm"
              icon="import"
            >
              Export
            </Button>
          </div>
        )}
      </div>
      <Panel
        mods={`${
          filterOpen ? '' : 'u-hidden'
        } u-padSm u-spaceTopSm u-borderNeutral4 u-bgNeutral1 Grid-cell u-flex`}
      >
        <FilterContext.Provider value={{ activeFilters, setActiveFilters }}>
          <FiltersSection
            filters={filters}
            setActiveFilters={setActiveFilters}
          />
        </FilterContext.Provider>
      </Panel>
    </div>
  );
}

const FiltersSection = ({
  filters,
  setActiveFilters,
}: {
  filters: any[];
  setActiveFilters: (f: Record<string, string[]>) => void;
}) => (
  <>
    <div className="u-size7of8">
      {filters.map((Item, index) => (
        <Item key={index} isLast={index === filters.length - 1} />
      ))}
    </div>
    <div className="u-size1of8 u-textRight u-spaceRightMd">
      <Button
        testId="PaginatedTable--filter-button-clear-all"
        type="text"
        onClick={() => {
          setActiveFilters({});
          // This is a bit weird.
          // We were seeing issues updating the components successfully when clearing out all the filter
          // values. This, paired with the condition in the filter rendering, forces the filter section
          // to rerender when we clear out all the filters, ensuring that its rendering with the "freshest"
          // of values from the context.
          // setIsResettingFilters(true);
        }}
      >
        Clear All
      </Button>
    </div>
  </>
);
